import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { outpostStyles, FadingVideo, BlurText, OutpostNavbar } from '../components/OutpostShared';

const FAQItem = ({ q, a }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="border-b border-black/10">
      <button 
        onClick={() => setIsOpen(!isOpen)} 
        className="w-full flex justify-between items-center py-6 text-left focus:outline-none group"
      >
        <span className="font-heading italic text-2xl md:text-3xl text-black pr-8 group-hover:text-black/70 transition-colors">{q}</span>
        <span className="text-black/40 flex-shrink-0 bg-black/5 rounded-full p-2 group-hover:bg-black/10 transition-colors">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={`w-5 h-5 transition-transform duration-300 ${isOpen ? 'rotate-45' : ''}`}>
            <path d="M12 5v14M5 12h14" />
          </svg>
        </span>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div 
            initial={{ height: 0, opacity: 0 }} 
            animate={{ height: 'auto', opacity: 1 }} 
            exit={{ height: 0, opacity: 0 }} 
            className="overflow-hidden"
          >
            <p className="pb-8 text-black/70 font-body text-lg leading-relaxed max-w-3xl pt-2">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function BrandDevelopment() {
  const items = [
    "Brand Guidelines", "Brand Identity", "Brand Positioning", 
    "Copywriting", "Logo Design", "Packaging Design", "Website Design"
  ];

  const problemStats = [
    { stat: "7 Secs", text: "To form a first impression of your brand — and 55% of that impression is visual." },
    { stat: "23%", text: "Average revenue increase from consistent brand presentation across all platforms." },
    { stat: "77%", text: "Of consumers make purchasing decisions based on a brand name alone." },
    { stat: "89%", text: "Of shoppers stay loyal to brands that clearly communicate shared values." }
  ];

  const intelligencePoints = [
    { title: "Brand Perception Audit", desc: "How your brand is currently perceived across channels, touchpoints, and audiences." },
    { title: "Competitive Landscape Analysis", desc: "Deep-dive into your competitors' positioning, visual identity, messaging, and market share." },
    { title: "Positioning Gap Identification", desc: "Where the white space exists for your brand to own a distinct, defensible position." },
    { title: "Visual & Messaging Consistency Scoring", desc: "How cohesive (or fragmented) your current brand expression is." },
    { title: "Strategic Recommendations", desc: "A prioritized roadmap of exactly what to build, fix, and amplify." }
  ];

  const capabilities = [
    { title: "Brand Guidelines", desc: "Comprehensive living documents that dictate typography, color palettes, spacing, and voice, ensuring consistency everywhere." },
    { title: "Brand Identity & Logo Design", desc: "Distinctive, enduring visual assets that capture your essence and scale perfectly from favicon to billboard." },
    { title: "Brand Positioning & Strategy", desc: "Strategic anchoring in the market that clearly defines who you are, who you serve, and why you're the superior choice." },
    { title: "Copywriting & Tone of Voice", desc: "A compelling verbal identity that dictates how your brand speaks, forming a deep emotional connection with your audience." },
    { title: "Packaging Design", desc: "Tactile, premium unboxing experiences that serve as the physical manifestation of your brand promise." },
    { title: "Website & Digital Experience", desc: "Immersive digital environments where your brand lives, breathes, and converts visitors into loyal advocates." }
  ];

  const benefits = [
    { icon: "💎", title: "Premium Perception", desc: "Command higher pricing power and industry authority through elevated, intentional design." },
    { icon: "🤝", title: "Unshakeable Loyalty", desc: "Build an emotional connection that turns casual buyers into lifelong brand advocates." },
    { icon: "🎯", title: "Targeted Clarity", desc: "Stop blending in. Communicate exactly what makes you different, to the people who care most." },
    { icon: "📐", title: "Flawless Consistency", desc: "A cohesive brand experience across every touchpoint—from social media to packaging." },
    { icon: "⚡", title: "Market Dominance", desc: "Own a distinct white space in your industry that competitors can't touch." },
    { icon: "📈", title: "Scalable Foundation", desc: "A robust brand system designed to grow effortlessly as you expand into new markets." }
  ];

  const processSteps = [
    { step: "01", title: "Audit", desc: "We analyze your current brand, market position, and competitors to find strategic gaps." },
    { step: "02", title: "Position", desc: "We define your core identity, values, target audience, and unique value proposition." },
    { step: "03", title: "Design", desc: "We craft the visual and verbal assets—logos, colors, typography, and voice." },
    { step: "04", title: "Systematize", desc: "We build comprehensive brand guidelines to ensure flawless consistency." },
    { step: "05", title: "Launch", desc: "We roll out your new identity across all touchpoints for maximum market impact." }
  ];

  const metrics = [
    { title: "First Impressions", value: "7s", desc: "The time it takes for a consumer to judge your brand visually." },
    { title: "Revenue Impact", value: "23%", desc: "Increase in revenue attributed to consistent brand presentation." },
    { title: "Value Alignment", value: "89%", desc: "Of shoppers stay loyal to brands that share their values." },
    { title: "Decision Making", value: "77%", desc: "Of consumers buy based on brand name and recognition alone." }
  ];

  const faqs = [
    { q: "Do we need a full rebrand or just a refresh?", a: "It depends on your current market position. A refresh updates your visual identity to stay modern, while a full rebrand overhauls your positioning, strategy, and visuals to signal a major shift or target a new audience. We'll help you decide during our initial audit." },
    { q: "How long does the branding process take?", a: "A comprehensive brand development project typically takes 6 to 12 weeks, depending on the scope of deliverables (e.g., adding packaging or complex website design). We don't rush strategy." },
    { q: "Will we own the final design files?", a: "Absolutely. Upon project completion and final payment, you receive full ownership and access to all source files, exported assets, and your brand guidelines." },
    { q: "We already have a logo. Can you just build the rest?", a: "Yes. If your logo is strong and aligned with your goals, we can build a cohesive brand system, guidelines, and extended visual identity around it." },
    { q: "How do you ensure the brand appeals to our target audience?", a: "We don't design based on personal preference. Every decision is rooted in our initial Brand Intelligence Report, merging competitor analysis with deep target audience profiling." }
  ];

  return (
    <div className="outpost-page min-h-screen text-black font-body selection:bg-black/10">
      <style>{outpostStyles}</style>
      <OutpostNavbar />

      {/* SECTION 1: Hero */}
      <section className="relative min-h-screen w-full flex flex-col items-center overflow-hidden pt-32 pb-24 px-8 md:px-16 lg:px-20">
        <FadingVideo 
          src="https://d8j0ntlcm91z4.cloudfront.net/user_38xzZboKViGWJOttwIXH07lWA1P/hf_20260418_094631_d30ab262-45ee-4b7d-99f3-5d5848c8ef13.mp4"
          className="absolute inset-0 w-full h-full object-cover z-0"
          maxOpacity={0.15}
        />

        <div className="relative z-10 w-full max-w-7xl mx-auto flex flex-col lg:flex-row gap-16 lg:gap-24 my-auto">
          
          {/* Left Text Block */}
          <div className="w-full lg:w-1/2 flex flex-col justify-center">
            <motion.div 
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
              className="text-sm font-body text-black/60 mb-6 uppercase tracking-widest"
            >
              // Expertise 01
            </motion.div>
            
            <BlurText 
              text="Brand Development" 
              justify="flex-start"
              className="text-6xl md:text-7xl lg:text-[5.5rem] font-heading italic text-black leading-[0.85] tracking-[-3px] mb-8 text-left"
            />
            
            <motion.p 
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4, duration: 0.8 }}
              className="text-lg text-black/80 font-body font-light leading-relaxed max-w-xl mb-10"
            >
              Forge an iconic identity that commands attention, evokes emotion, and endures. From strategic positioning and data-driven brand intelligence to meticulous visual identity and packaging design — we architect brands built to dominate crowded markets and scale with purpose.
            </motion.p>

            <div className="flex flex-col gap-4 mb-10">
              <h4 className="font-heading italic text-2xl text-black mb-4">Core Focus</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-8">
                {items.map((item, i) => (
                  <motion.div 
                    key={item}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 + (i * 0.1) }}
                    className="flex items-center gap-3"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-black/40 shrink-0"></span>
                    <span className="text-sm font-medium uppercase tracking-wider text-black/80">{item}</span>
                  </motion.div>
                ))}
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2, duration: 0.8 }}
            >
              <Link to="/contact" className="bg-black text-white rounded-full px-7 py-3.5 font-body font-medium flex items-center gap-2 hover:bg-black/80 transition-colors w-fit shadow-lg shadow-black/10">
                Start Building Your Brand
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 ml-1"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
              </Link>
            </motion.div>
          </div>

          {/* Right Media Grid */}
          <div className="w-full lg:w-1/2 grid grid-cols-1 sm:grid-cols-2 gap-6 relative items-center">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-white/20 blur-3xl rounded-full -z-10"></div>
            
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.8 }} className="relative w-full aspect-[4/5] bg-black/5 rounded-[1.25rem] overflow-hidden shadow-xl border border-black/10 group sm:mt-12">
              <img src="https://media-manager-c.questera.ai/greta-media/b729ed0728dd00b56ad4432b7bed08bb6e0f1b37b57acc2ecba8e8caa91ad92afaa368f7ee662627c72dbcd9a47643b4/images/aW1hZ2UvcG5n/a630f1cf7f58fa47c55ea6c6fca71e3e.png" alt="Zest & Root Brand Identity" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            </motion.div>
            
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8, duration: 0.8 }} className="relative w-full aspect-[4/5] bg-black/5 rounded-[1.25rem] overflow-hidden shadow-xl border border-black/10 group">
              <img src="https://media-manager-c.questera.ai/greta-media/b729ed0728dd00b56ad4432b7bed08bb6e0f1b37b57acc2ecba8e8caa91ad92afaa368f7ee662627c72dbcd9a47643b4/images/aW1hZ2UvcG5n/f4189ce5732349782dea3cc7b1a8c921.png" alt="Dew & Stem Brand Identity" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* SECTION 2: Problem Statement */}
      <section className="relative w-full py-24 md:py-32 px-8 md:px-16 lg:px-20 bg-black/[0.02] border-y border-black/[0.05]">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="mb-16 max-w-4xl"
          >
            <h2 className="font-heading italic text-4xl md:text-5xl text-black mb-6">Why Most Brands Fade Into the Background</h2>
            <p className="text-black/70 font-body text-lg leading-relaxed">
              A brand isn't a logo. It's a system of perception — and every detail either builds equity or erodes it. Brands without documented guidelines experience 25–40% more inconsistency in customer-facing materials, diluting trust and recognition over time. <br/><br/><strong>We make sure every detail works.</strong>
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {problemStats.map((stat, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="liquid-glass rounded-2xl p-8 flex flex-col border border-black/5 hover:border-black/10 transition-colors shadow-sm"
              >
                <span className="font-heading italic text-5xl text-black mb-4">{stat.stat}</span>
                <p className="text-black/70 font-body text-sm leading-relaxed">{stat.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 3: Our Differentiator (Split Section) */}
      <section className="relative w-full py-24 md:py-32 px-8 md:px-16 lg:px-20">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-16 lg:gap-24 items-center">
          <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="w-full lg:w-1/2">
            <h2 className="font-heading italic text-4xl md:text-5xl text-black mb-6">Strategy First. Always.</h2>
            <p className="text-black/70 font-body text-lg leading-relaxed mb-6">
              Unlike agencies that jump straight to design, we start every engagement with a comprehensive Brand Intelligence Report — a proprietary, data-driven audit that analyzes your current brand, benchmarks you against competitors, and identifies the strategic gaps standing between you and market leadership.
            </p>
            <p className="text-black/70 font-body text-lg leading-relaxed mb-8">
              This isn't guesswork. It's a rigorous analysis covering:
            </p>
            <div className="flex flex-col gap-6 mb-10">
              {intelligencePoints.map((pt, i) => (
                <div key={i} className="flex gap-4">
                  <span className="w-1.5 h-1.5 rounded-full bg-black/40 mt-2 shrink-0"></span>
                  <div>
                    <h4 className="font-body font-semibold text-black mb-1">{pt.title}</h4>
                    <p className="text-black/60 font-body text-sm leading-relaxed">{pt.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-black font-body text-lg leading-relaxed mb-10 italic font-heading">
              "Every design decision, every word of copy, every brand guideline we create is rooted in the insights from this report. The result? A brand that isn't just beautiful — it's strategically engineered to win."
            </p>
            <Link to="/contact" className="bg-white text-black border border-black/10 rounded-full px-7 py-3.5 font-body font-medium flex items-center gap-2 hover:bg-black/5 transition-colors w-fit shadow-sm">
              See What a Brand Audit Reveals
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 ml-1"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
            </Link>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="w-full lg:w-1/2">
            <div className="relative w-full aspect-[4/5] bg-black/5 rounded-[2rem] overflow-hidden shadow-xl border border-black/10 group">
               <img src="https://media-manager-c.questera.ai/greta-media/b729ed0728dd00b56ad4432b7bed08bb6e0f1b37b57acc2ecba8e8caa91ad92afaa368f7ee662627c72dbcd9a47643b4/images/aW1hZ2UvcG5n/1add3c0c065ee169ee6787f705074bfa.png" alt="Aether Brand Identity" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* SECTION 4: Capabilities */}
      <section className="relative w-full py-24 md:py-32 px-8 md:px-16 lg:px-20 bg-black/[0.02] border-y border-black/[0.05]">
        <div className="max-w-7xl mx-auto">
           <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-16 text-center">
            <h2 className="font-heading italic text-4xl md:text-5xl text-black mb-6">Core Capabilities</h2>
            <p className="text-black/70 font-body text-lg max-w-2xl mx-auto">A comprehensive ecosystem of brand assets designed to tell a cohesive, compelling story.</p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 justify-center">
            {capabilities.map((cap, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="bg-white rounded-2xl p-8 flex flex-col shadow-sm border border-black/5 hover:border-black/10 transition-colors"
              >
                <h4 className="font-heading italic text-2xl text-black mb-3">{cap.title}</h4>
                <p className="text-black/70 font-body text-sm leading-relaxed">{cap.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4.5: Featured Systems */}
      <section className="relative w-full py-24 md:py-32 px-8 md:px-16 lg:px-20">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-16 text-center">
            <h2 className="font-heading italic text-4xl md:text-5xl text-black mb-6">Designed to Dominate</h2>
            <p className="text-black/70 font-body text-lg max-w-2xl mx-auto">Visual systems that communicate value before a single word is read.</p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="rounded-[2rem] overflow-hidden shadow-2xl border border-black/10 group aspect-[3/4]">
              <img src="https://media-manager-c.questera.ai/greta-media/b729ed0728dd00b56ad4432b7bed08bb6e0f1b37b57acc2ecba8e8caa91ad92afaa368f7ee662627c72dbcd9a47643b4/images/aW1hZ2UvcG5n/1f657414ece9f3455a2c5f5fa2d12848.png" alt="Glaze Lab Brand Identity" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }} className="rounded-[2rem] overflow-hidden shadow-2xl border border-black/10 group aspect-[3/4] md:mt-24">
              <img src="https://media-manager-c.questera.ai/greta-media/b729ed0728dd00b56ad4432b7bed08bb6e0f1b37b57acc2ecba8e8caa91ad92afaa368f7ee662627c72dbcd9a47643b4/images/aW1hZ2UvcG5n/264790de4c8ee62a36fc04cfdb8313e0.png" alt="Terra Step Brand Identity" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* SECTION 5: Benefits */}
      <section className="relative w-full py-24 md:py-32 px-8 md:px-16 lg:px-20 bg-black/[0.02] border-y border-black/[0.05]">
         <div className="max-w-7xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-16 max-w-2xl">
               <h2 className="font-heading italic text-4xl md:text-5xl text-black mb-6">What You Can Expect</h2>
            </motion.div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {benefits.map((b, i) => (
                <motion.div 
                  key={i}
                  initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                  className="bg-white rounded-2xl p-8 flex flex-col shadow-sm border border-black/5"
                >
                  <div className="text-3xl mb-4">{b.icon}</div>
                  <h4 className="font-body font-semibold text-black text-lg mb-2">{b.title}</h4>
                  <p className="text-black/70 font-body text-sm leading-relaxed">{b.desc}</p>
                </motion.div>
              ))}
            </div>
         </div>
      </section>

      {/* SECTION 6: Process */}
      <section className="relative w-full py-24 md:py-32 px-8 md:px-16 lg:px-20">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-16 text-center">
            <h2 className="font-heading italic text-4xl md:text-5xl text-black mb-6">Our Process</h2>
            <p className="text-black/70 font-body text-lg max-w-2xl mx-auto">Meticulous methodology ensuring every creative decision is backed by strategy.</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {processSteps.map((s, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="liquid-glass rounded-2xl p-6 flex flex-col shadow-sm border border-black/5"
              >
                <div className="flex items-center justify-between mb-8">
                  <span className="text-sm font-body uppercase tracking-widest text-black/40">Step</span>
                  <span className="text-3xl font-heading italic text-black/30">{s.step}</span>
                </div>
                <h4 className="font-body font-semibold text-black text-lg mb-3">{s.title}</h4>
                <p className="text-black/70 font-body text-sm leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 7: Metrics */}
      <section className="relative w-full py-24 md:py-32 px-8 md:px-16 lg:px-20 bg-black/[0.02] border-y border-black/[0.05]">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-16 items-center">
          <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="w-full lg:w-1/3">
            <h2 className="font-heading italic text-4xl md:text-5xl text-black mb-6">The Numbers That Matter</h2>
            <p className="text-black/70 font-body text-lg leading-relaxed mb-6">
              Strong brands aren't just aesthetic; they are serious financial assets that multiply the effectiveness of your marketing and sales.
            </p>
          </motion.div>
          
          <div className="w-full lg:w-2/3 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {metrics.map((metric, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="bg-white rounded-2xl p-8 border border-black/5 flex flex-col shadow-sm"
              >
                <h4 className="font-body text-sm uppercase tracking-widest text-black/50 mb-2">{metric.title}</h4>
                <span className="font-heading italic text-5xl text-black mb-4">{metric.value}</span>
                <p className="text-black/70 font-body text-sm leading-relaxed">{metric.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 8: FAQ */}
      <section className="relative w-full py-24 md:py-32 px-8 md:px-16 lg:px-20">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-16 text-center">
            <h2 className="font-heading italic text-4xl md:text-5xl text-black">Common Questions</h2>
          </motion.div>

          <div className="flex flex-col">
            {faqs.map((faq, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}>
                <FAQItem q={faq.q} a={faq.a} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 9: Final CTA */}
      <section className="relative w-full py-32 px-8 md:px-16 lg:px-20 flex justify-center">
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}
          className="liquid-glass-strong rounded-[2.5rem] p-12 md:p-24 w-full max-w-6xl text-center flex flex-col items-center border border-black/10 shadow-2xl shadow-black/5 relative overflow-hidden"
        >
          <div className="absolute inset-0 bg-gradient-to-b from-white/40 to-transparent pointer-events-none"></div>
          
          <h2 className="font-heading italic text-5xl md:text-7xl text-black mb-8 relative z-10">Ready to Build an Iconic Brand?</h2>
          <p className="text-black/70 font-body max-w-2xl text-lg md:text-xl mb-12 relative z-10">
            Stop blending into the background. Let's engineer a brand identity that commands attention, builds deep trust, and drives measurable growth.
          </p>
          <Link to="/contact" className="bg-black text-white rounded-full px-8 py-4 font-body font-medium flex items-center gap-2 hover:bg-black/80 transition-colors shadow-xl shadow-black/20 relative z-10 text-lg">
            Start Your Brand Transformation
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 ml-1"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
          </Link>
        </motion.div>
      </section>

    </div>
  );
}